/* @flow */
import * as React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Buttons from 'components/Buttons';
import Row from 'components/Row';
import SmartTableHeaderColumn from 'components/SmartTableHeaderColumn';
import SmartTableSearchInput from 'components/SmartTableSearchInput';
import SmartTableFilterTokens from 'components/SmartTableFilterTokens';
import SmartTableFilterIcon from 'components/SmartTableFilterIcon';
import Table from 'components/Table';
import { bindActionCreators } from 'redux';
import { initTable } from 'reducers/tables';
import { connect } from 'react-redux';
import invariant from 'invariant';
import { endsWith } from 'lodash';

import './styles/SmartTable.scss';

import type { Filter as FilterOption } from 'reducers/tables';

type FilterKey = string;

export type Filter = {
  name: FilterKey,
  options: FilterOption[],
};

type SortKey = string;
type SortDirection = 'asc' | 'desc';

export function sortDirection(key: SortKey): ?SortDirection {
  if (endsWith(key, 'asc')) {
    return 'asc';
  }
  if (endsWith(key, 'desc')) {
    return 'desc';
  }
  return null;
}

export type Sort = {
  name: string,
  key: SortKey,
  lastInSection?: boolean,
};

type ColumnKey = string;

export type Column = {
  key: ColumnKey,
  name: string,
  sorts?: Sort[],
  simpleSort?: boolean,
  tooltip?: string,
};

type Props = {
  className?: string,
  headerActions?: any,
  searchPlaceholder?: string,
  /* Array of possible filters.
   * When undefined filters button will not be rendered.
   * A Set of selected filter keys will passed down to the children.
   */
  filters?: Filter[],
  /* Array of columns, if some columns have sorts:
   * A Map of of selected sort: column_key <==> filter_key will be passed down to the children.
   */
  columns: Column[],
  /* namespace of the table.
   * Table settings (query, sorts, filters) are stored in the redux stored
   * namespaced by this key. You'll usually want this to be a constant in the parent.
   */
  namespace: string,
  /* withSearch - when true render the search input.
   * Passing filters without search input of not supported yet (design).
   */
  withSearch: boolean,
  /* Children function that take an object describing the table settings as argument.
   * You usually want to render a <tbody> in this children.
   */
  children?: ({ query: string, filters: Set<FilterKey>, sort?: string }) => any, // eslint-disable-line
  /* Initial sort key.
   * This is usefull is one of the columns sort is the default one (because you want it checked by default).
   */
  initialSort?: string,
  /* Pull the last column to the right
   */
  hasActions?: boolean,

  /* Redux props: */
  currentFilters?: Set<FilterKey>,
  currentSort?: string,
  currentQuery?: string,
  initialized?: boolean,
  initTable: (namespace: string, options?: { sort?: string }) => *,
};

class SmartTable extends React.Component<Props> {
  static childContextTypes = {
    namespace: PropTypes.string.isRequired,
  };

  static defaultProps = {
    searchPlaceholder: 'Search',
    withSearch: false,
  };

  getChildContext() {
    return {
      namespace: this.props.namespace,
    };
  }

  componentWillMount = () => {
    if (!this.props.initialized) {
      this.props.initTable(this.props.namespace, { sort: this.props.initialSort });
    }
  };

  render() {
    if (!this.props.initialized) {
      return <div />;
    }

    const { children, currentQuery, currentSort, currentFilters } = this.props;

    invariant(children, 'table header children is required');
    invariant(
      this.props.withSearch || (!this.props.filters || this.props.filters.length === 0),
      'cant filter without search input'
    );
    invariant(
      typeof currentQuery === 'string',
      'SmartTable should not try to render without query (not initialized?)'
    );
    invariant(
      currentFilters,
      'SmartTable should not try to render without filters (not initialized?)'
    );

    const columns = this.props.columns.map(column => (
      <SmartTableHeaderColumn key={column.key} column={column} sortKey={this.props.currentSort} />
    ));

    return (
      <div className={cx('SmartTable', this.props.className)}>
        {(this.props.withSearch || !!this.props.filters || !!this.props.headerActions) && (
          <Row size="small">
            <header className="SmartTable-header">
              <div className="SmartTable-filterSearch">
                {this.props.withSearch && (
                  <SmartTableSearchInput
                    namespace={this.props.namespace}
                    placeholder={this.props.searchPlaceholder}
                  />
                )}
                {this.props.filters &&
                  this.props.filters.length > 0 && (
                    <SmartTableFilterIcon filters={this.props.filters} />
                  )}
              </div>
              {this.props.headerActions && (
                <Buttons align="right">{this.props.headerActions}</Buttons>
              )}
            </header>
          </Row>
        )}
        {this.props.filters && <SmartTableFilterTokens namespace={this.props.namespace} />}
        <Table
          actionsColumn={this.props.hasActions}
          head={<tr>{columns}</tr>}
          className="SmartTable-table">
          {children({ query: currentQuery, sort: currentSort, filters: currentFilters })}
        </Table>
      </div>
    );
  }
}

function mapStateToProps(state, props: Props) {
  const table = state.tables.get(props.namespace);

  if (!table) {
    return {
      initialized: false,
    };
  }

  return {
    initialized: true,
    currentSort: table.sort,
    currentQuery: table.query,
    currentFilters: table.filters,
  };
}

function mapDispatchToProps(dispatch: Function) {
  // $FlowFixMe
  return bindActionCreators({ initTable }, dispatch);
}

export { SmartTable };
// $FlowFixMe
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SmartTable);
